import { IPrintJobDto, IWebsiteIntegration } from "@promotron/website-integration-types";
import { devLog } from "../common";
import { stringLocalizer } from "../main";
import { getTronDesignerAsync } from "./tron-designer";

// Enriches the cart's design rows with LIVE Print Job data from TronDesigner (td.getPrintJob):
// position/technology display names, the design's real size and colors, the motive thumbnail, and the
// per-printing validationMessages (INFO/WARNING/ERROR) — the SDK returns those already translated, so
// their text is fully dynamic and never comes from shop resources. The server only renders an empty
// [data-td-design-details] placeholder carrying its own §4.4 spec-match verdict as data attributes
// (that check compares against the shop's priced config, which TronDesigner knows nothing about).

type Printing = IPrintJobDto["printings"][number];
type Severity = "ok" | "warn" | "err";

function t(key: string, ...args: (string | number)[]): string {
    let value = stringLocalizer?.get(key) ?? `Missing.${key}`;
    args.forEach((arg, i) => { value = value.replace(`{${i}}`, String(arg)); });
    return value;
}

export function initCartDesignInfo(): void {
    document.querySelectorAll<HTMLElement>(".cart-preview-printing-set[data-td-print-job-guid]").forEach(set => {
        if (set.dataset.tdDesignInfoBound) return;
        set.dataset.tdDesignInfoBound = "1";

        const printJobGuid = set.dataset.tdPrintJobGuid;
        if (!printJobGuid) return;
        const rows = Array.from(set.querySelectorAll<HTMLElement>("[data-td-design-details]"));

        // Skeleton shimmer (CSS) while the print job loads; every exit path clears it — on failure the
        // rows fall back to the :empty hide instead of shimmering forever.
        rows.forEach(row => row.classList.add("is-loading"));
        void enrichPrintSet(printJobGuid, rows, set);
    });
}

async function enrichPrintSet(printJobGuid: string, rows: HTMLElement[], set: HTMLElement): Promise<void> {
    const stopLoading = (): void => rows.forEach(row => row.classList.remove("is-loading"));
    let td: IWebsiteIntegration;
    let printJob: IPrintJobDto | null | undefined;
    try {
        td = await getTronDesignerAsync();
        printJob = await td.getPrintJob(printJobGuid);
    } catch (err) {
        stopLoading();
        // A visible error, not devLog: this failure blanks the whole design row (facts, tag, thumbnail)
        // and would otherwise be undiagnosable in the field (SDK missing/failed on the cart page, network).
        console.error("TronDesigner: could not load the print job for the cart design info —", printJobGuid, err);
        return;
    }

    // Piece-count check against the LIVE print job — the calculation's total pieces (data-td-calc-count,
    // stamped server-side) vs the sum of the job's product.variants quantities. Runs independently of the
    // per-printing enrichment below, so it warns even for jobs whose §4.4 snapshot was never captured.
    checkPieceCount(set, printJob);

    if (!printJob?.printings?.length) {
        stopLoading();
        console.error("TronDesigner: print job has no printings — cart design info stays empty.", { printJobGuid, printJob });
        return;
    }

    // Match each design row to its printing by TCC position+technology codes; consume matches so
    // duplicated positions pair one-to-one (same rule as the server-side §4.4 comparison).
    const remaining = [...printJob.printings];
    const unmatchedRows: HTMLElement[] = [];
    const matches = new Map<HTMLElement, Printing>();

    for (const row of rows) {
        // Rows without codes (no server-side spec snapshot) go to the order-based fallback.
        if (!row.dataset.tdPositionCode && !row.dataset.tdTechnologyCode) {
            unmatchedRows.push(row);
            continue;
        }
        const idx = remaining.findIndex(p =>
            codeEquals(p.printData?.positionCodePtn, row.dataset.tdPositionCode) &&
            codeEquals(p.printData?.technologyCodePtn, row.dataset.tdTechnologyCode));
        if (idx >= 0) {
            matches.set(row, remaining.splice(idx, 1)[0]);
        } else {
            unmatchedRows.push(row);
        }
    }

    // Fallback: pair leftovers by order. Covers designs linked without a config snapshot and shops where
    // the TCC codes don't line up with TD's codePtn namespace — the facts/thumbnail still display, only
    // the code-precise pairing is lost (single-motive sets, the common case, are unaffected either way).
    for (const row of unmatchedRows) {
        const printing = remaining.shift();
        if (printing) {
            matches.set(row, printing);
        }
    }

    stopLoading();
    for (const row of rows) {
        const printing = matches.get(row);
        renderRow(row, printing);
        if (printing) {
            void renderThumbnail(td, printJobGuid, printing, row);
        }
    }
}

function codeEquals(a?: string, b?: string): boolean {
    return (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();
}

function renderRow(row: HTMLElement, printing?: Printing): void {
    row.textContent = "";

    const facts = el("span", "cart-card__design-facts");
    facts.appendChild(sourceLabel());

    if (printing) {
        const data = printing.printData;
        const motive = printing.printMotive;
        // Per-chip warn state comes from the server's §4.4 spec verdict (data attributes) — the chip
        // whose value breaks the priced config is highlighted, mirroring the design template.
        const sizeWarn = !!row.dataset.tdSpecArea;
        const colorsWarn = !!row.dataset.tdSpecColors;
        if (data?.positionName) facts.appendChild(fact(t("TdPosition"), data.positionName));
        if (data?.technologyName) facts.appendChild(fact(t("TdTechnology"), data.technologyName));
        if (motive && motive.widthMm > 0 && motive.heightMm > 0) {
            facts.appendChild(fact(t("TdActualSize"), `${formatMm(motive.widthMm)} × ${formatMm(motive.heightMm)} mm`, sizeWarn));
        }
        if (motive) {
            if (motive.colorList?.length) {
                // Show the actual chosen colours as small swatch circles beside the count, not just a number.
                facts.appendChild(colorsFact(motive.colorList, colorsWarn));
            } else if (data?.isFullColor) {
                facts.appendChild(fact(t("TdColors"), t("TdFullColor"), colorsWarn));
            } else {
                // CART-00: e.g. a full-colour logo on a 1-colour technology — the design resolves to zero
                // pantone colours. Still render the Colors fact ("No colors in motive") so the detail is
                // complete rather than silently missing.
                facts.appendChild(fact(t("TdColors"), t("TdNoColorsInMotive"), colorsWarn));
            }
        }
    }
    row.appendChild(facts);
    row.appendChild(buildTag(row, printing));
}

function sourceLabel(): HTMLElement {
    const label = el("span", "cart-card__design-source");
    label.innerHTML = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 1.5a6.5 6.5 0 1 0 0 13c.9 0 1.4-.6 1.4-1.3 0-.6-.4-1-.4-1.6 0-.8.6-1.4 1.5-1.4h1.6a2.4 2.4 0 0 0 2.4-2.4A6.6 6.6 0 0 0 8 1.5Z"/><path d="M5 5.2h.01M8.2 4h.01M11.2 5.4h.01M4.4 8.6h.01"/></svg>';
    label.appendChild(document.createTextNode(` ${t("TdFromYourDesign")}`));
    return label;
}

function fact(label: string, value: string, warn = false): HTMLElement {
    const span = el("span", `cart-card__param${warn ? " cart-card__param--warn" : ""}`);
    span.appendChild(document.createTextNode(`${label}: `));
    const strong = document.createElement("strong");
    strong.textContent = value;
    span.appendChild(strong);
    return span;
}

type MotiveColor = NonNullable<Printing["printMotive"]>["colorList"][number];

// A CSS-usable colour from the SDK's hex string: "FF0000" / "#FF0000" both work; anything already a named
// colour or rgb() passes through, and empties fall back to a neutral so the swatch is never invisible-white.
function toCssColor(raw?: string): string {
    const v = (raw ?? "").trim();
    if (!v) return "";
    if (v.startsWith("#") || v.startsWith("rgb")) return v;
    return /^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(v) ? `#${v}` : v;
}

// The "Colors" chip, enriched with a swatch circle per chosen colour (PMS name as the hover title) plus the
// count. Mirrors the variant-row swatch language so the design's palette reads at a glance.
function colorsFact(colorList: MotiveColor[], warn = false): HTMLElement {
    const span = el("span", `cart-card__param cart-card__param--colors${warn ? " cart-card__param--warn" : ""}`);
    span.appendChild(document.createTextNode(`${t("TdColors")}: `));

    const dots = el("span", "cart-card__color-dots");
    colorList.forEach(c => {
        const dot = el("span", "cart-card__color-dot");
        const css = toCssColor(c.rgbHex);
        if (css) dot.style.backgroundColor = css;
        const name = c.pmsName || css;
        if (name) dot.title = name;
        dots.appendChild(dot);
    });
    span.appendChild(dots);

    const strong = document.createElement("strong");
    strong.textContent = String(colorList.length);
    span.appendChild(strong);
    return span;
}

// One combined status tag per design: the worst of TronDesigner's own validationMessages (dynamic,
// translated by the SDK) and the shop's §4.4 spec-match verdict from the data attributes.
function buildTag(row: HTMLElement, printing?: Printing): HTMLElement {
    const messages = printing?.validationMessages ?? [];
    const hasError = messages.some(m => m.severity === "ERROR");
    const hasWarning = messages.some(m => m.severity === "WARNING");
    const specMismatch = row.dataset.tdSpecMismatch === "1";
    // SET-05/6: a set-level variant/quantity mismatch must not leave the design tag claiming everything
    // matches — the tag and its spec section warn alongside the red group chip.
    const groupKind = groupMismatchKind(row);

    const severity: Severity = hasError ? "err" : (hasWarning || specMismatch || groupKind !== null ? "warn" : "ok");
    const label = severity === "err" ? t("TdDesignError") : severity === "warn" ? t("TdDesignWarning") : t("TdDesignValid");

    const tag = el("span", `cart-card__design-tag cart-card__design-tag--${severity}`);
    tag.tabIndex = 0;
    tag.innerHTML = severity === "ok"
        ? '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="8" cy="8" r="6.5"/><path d="m5.2 8.2 2 2 3.6-4.2"/></svg>'
        : '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2 1.5 13.5h13L8 2Z"/><path d="M8 7v3M8 12.2h.01"/></svg>';
    tag.appendChild(document.createTextNode(` ${label} `));
    tag.insertAdjacentHTML("beforeend",
        '<svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="cart-card__design-tag-info"><circle cx="8" cy="8" r="6.5"/><path d="M8 7.5v3.5"/><path d="M8 5h.01"/></svg>');

    tag.appendChild(buildTip(row, messages, specMismatch, groupKind));
    return tag;
}

// The print set's §4.4 mismatch kind ("variants" | "quantity" | "config"), read from the set root's
// data-td-mismatch-kind (stamped server-side). Null when the set has no flagged mismatch.
function groupMismatchKind(row: HTMLElement): string | null {
    return row.closest<HTMLElement>(".cart-preview-printing-set")?.dataset.tdMismatchKind ?? null;
}

function buildTip(row: HTMLElement, messages: Printing["validationMessages"], specMismatch: boolean, groupKind: string | null): HTMLElement {
    const tip = el("span", "cart-card__design-tip");
    tip.setAttribute("role", "tooltip");

    // Group 1 — the shop's spec-match check (design vs the PRICED print options; TronDesigner cannot
    // know these). The comparison values come from the server via data attributes. When the server had
    // no snapshot to compare against (attribute absent), the section is omitted — an "OK" would be a
    // claim nothing actually verified. A set-level variant/quantity mismatch (SET-05/6) also belongs
    // here: "Matches print specifications" must never show while the group chip is red.
    const specVerdictKnown = row.dataset.tdSpecMismatch !== undefined;
    const setMismatch = groupKind === "variants" || groupKind === "quantity";
    if (specVerdictKnown || setMismatch) {
        tip.appendChild(tipHeading(specMismatch || setMismatch ? "warn" : "ok", t(specMismatch || setMismatch ? "TdSpecMismatchHeading" : "TdSpecMatchHeading")));
        if (specMismatch) {
            const colors = row.dataset.tdSpecColors?.split("|");
            const area = row.dataset.tdSpecArea?.split("|");
            if (colors?.length === 2) tip.appendChild(tipLine(t("TdSpecColorsExceeded", colors[0], colors[1])));
            if (area?.length === 2) tip.appendChild(tipLine(t("TdSpecAreaExceeded", area[0], area[1])));
            if (row.dataset.tdSpecConfigChanged === "1") tip.appendChild(tipLine(t("TdSpecConfigChanged")));
        }
        if (groupKind === "variants") {
            tip.appendChild(tipLine(t("TdVariantsMismatch")));
        } else if (groupKind === "quantity") {
            tip.appendChild(tipLine(t("TdQuantityMismatchTip")));
        }
        if (!specMismatch && !setMismatch) {
            tip.appendChild(tipLine(t("TdSpecMatchOk")));
        }
    }

    // Group 2 — TronDesigner's own design validation, verbatim (already localized by the SDK).
    const worst: Severity = messages.some(m => m.severity === "ERROR") ? "err"
        : messages.some(m => m.severity === "WARNING") ? "warn" : "ok";
    tip.appendChild(tipHeading(worst, t("TdDesignCheckHeading")));
    if (messages.length) {
        messages.forEach(m => tip.appendChild(tipLine(m.message)));
    } else {
        tip.appendChild(tipLine(t("TdDesignCheckOk")));
    }

    return tip;
}

function tipHeading(severity: Severity, text: string): HTMLElement {
    const heading = el("span", `cart-card__design-tip-heading cart-card__design-tip-heading--${severity}`);
    heading.textContent = text;
    return heading;
}

function tipLine(text: string): HTMLElement {
    const line = el("span", "cart-card__design-tip-line");
    line.textContent = text;
    return line;
}

// Browser-renderable motive formats — for these the DTO's pre-authorized URL can go straight into
// an <img>; other formats (PDF/AI/EPS/CDR/...) fall back to the SDK's rendered blob.
const WEB_IMAGE_FORMATS = new Set(["SVG", "PNG", "JPG", "WEBP"]);

function isRenderableUrl(file?: { printJobFileUrl?: string; format?: unknown }): boolean {
    return !!file?.printJobFileUrl && WEB_IMAGE_FORMATS.has(String(file.format).toUpperCase());
}

// The design thumbnail. Spec §2 asks for "the first PJ image/visualisation, if not — just the logo":
// prefer the printing's visualization (the product WITH the design on it), then a mockup, then the raw
// motive artwork, and finally the SDK-rendered motive blob. The image lands in the panel's media-row
// box ([data-td-design-thumb], server-rendered with the uploaded-logo/placeholder fallback). Best-effort —
// the box simply keeps its fallback when nothing renders.
async function renderThumbnail(td: IWebsiteIntegration, printJobGuid: string, printing: Printing, row: HTMLElement): Promise<void> {
    const box = row.closest<HTMLElement>(".cart-card__design")?.querySelector<HTMLElement>("[data-td-design-thumb]");
    const place = (img: HTMLImageElement): void => {
        if (box) {
            box.classList.remove("cart-card__design-thumb--empty");
            box.textContent = "";
            box.appendChild(img);
        } else {
            row.insertBefore(img, row.firstChild);
        }
    };

    const areaImages = printing.printMotive?.printAreaImages ?? [];
    const motiveFile = printing.printMotive?.motiveImage;
    const candidates = [
        areaImages.find(i => i.type === "VISUALIZATION")?.printAreaImageFile,
        areaImages.find(i => i.type === "MOCKUP" || i.type === "MOCKUP_CLEAR")?.printAreaImageFile,
        motiveFile,
    ];

    // Fallback path: ask the SDK to render the motive to a blob. Kept as a nested helper so the direct-URL
    // path can fall through to it when the pre-authorized URL is stale/broken.
    const renderFromSdkBlob = async (): Promise<void> => {
        try {
            const blob = await td.getPrintingMotive(printJobGuid, printing.printingGuid);
            if (!blob) return;
            const objectUrl = URL.createObjectURL(blob);
            const blobImg = document.createElement("img");
            blobImg.alt = "";
            blobImg.addEventListener("load", () => {
                URL.revokeObjectURL(objectUrl);
                if (motiveFile?.shouldUseDarkBackground) {
                    box?.classList.add("cart-card__design-thumb--dark");
                }
                place(blobImg);
            }, { once: true });
            blobImg.addEventListener("error", () => URL.revokeObjectURL(objectUrl), { once: true });
            blobImg.src = objectUrl;
        } catch (err) {
            devLog("TronDesigner: getPrintingMotive failed for cart thumbnail", { printJobGuid, err });
        }
    };

    const file = candidates.find(isRenderableUrl);
    if (file) {
        const img = document.createElement("img");
        img.alt = "";
        // Attach the image only once it has actually loaded — otherwise a stale/broken printJobFileUrl
        // would wipe the box's server-rendered placeholder and leave it blank. On error, fall through to
        // the SDK blob rather than removing the img and giving up.
        img.addEventListener("load", () => {
            if (file.shouldUseDarkBackground) {
                box?.classList.add("cart-card__design-thumb--dark");
            }
            place(img);
        }, { once: true });
        img.addEventListener("error", () => { void renderFromSdkBlob(); }, { once: true });
        img.src = file.printJobFileUrl;
        return;
    }

    await renderFromSdkBlob();
}

function formatMm(value: number): string {
    return (Math.round(value * 10) / 10).toString();
}

// Compare the calculation's ordered pieces (server-rendered) with the linked design's own pieces (the sum
// of the live Print Job's product-variant quantities). A drift means the design was made for a different
// quantity than is now in the cart — surface a warning so the customer opens the design to update it.
function checkPieceCount(set: HTMLElement, printJob: IPrintJobDto | null | undefined): void {
    // SET-05/6: when the server's §4.4 verdict says the VARIANT SET differs (a variant added/removed),
    // a raw "21 vs 10 pcs" line would name the wrong problem — the counts differ BECAUSE the sets
    // differ. Name the real mismatch instead.
    if (set.dataset.tdMismatchKind === "variants") {
        renderVariantsWarning(set);
        return;
    }

    const calc = parseInt(set.dataset.tdCalcCount ?? "", 10);
    const pjPieces = (printJob?.product?.variants ?? [])
        .reduce((sum, v) => sum + (v.quantity ?? 0), 0);

    // Only warn on a real, known difference; a missing/zero count on either side is "unknown", not a warning.
    if (!Number.isFinite(calc) || calc <= 0 || pjPieces <= 0 || calc === pjPieces) {
        removeCountWarning(set);
        return;
    }
    renderCountWarning(set, calc, pjPieces);
}

function removeCountWarning(set: HTMLElement): void {
    set.querySelector(".cart-card__count-warning")?.remove();
}

function renderCountWarning(set: HTMLElement, calc: number, pj: number): void {
    renderSetWarning(set, t("TdCountMismatch", calc, pj));
}

// SET-05/6: the variant-set mismatch wording ("variants in your order don't match the design").
function renderVariantsWarning(set: HTMLElement): void {
    renderSetWarning(set, t("TdVariantsMismatch"));
}

function renderSetWarning(set: HTMLElement, text: string): void {
    removeCountWarning(set);
    const warn = el("div", "cart-card__count-warning");
    warn.setAttribute("role", "status");
    warn.innerHTML = '<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2 1.5 13.5h13L8 2Z"/><path d="M8 6.2v3M8 11.2h.01"/></svg>';
    warn.appendChild(document.createTextNode(" " + text));

    // Sit right below the print-set header so it is always visible, above the (collapsible) design body.
    const head = set.querySelector<HTMLElement>(".cart-card__section-head");
    if (head) {
        head.insertAdjacentElement("afterend", warn);
    } else {
        set.insertBefore(warn, set.firstChild);
    }
}

function el(tagName: string, className: string): HTMLElement {
    const node = document.createElement(tagName);
    node.className = className;
    return node;
}
